import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import MarkdownRenderer from './MarkdownRenderer';
import './Rag.css';

type ModelType = 'gpt' | 'gemini' | 'claude' | 'grok';

interface ModelResponse {
  reply?: string;
  model?: string;
  durationSec?: number;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

interface OrderedResponse {
  provider: ModelType;
  data?: ModelResponse;
  loading: boolean;
  order?: number;
}

interface Message {
  id: string;
  role: 'user' | 'bot';
  text: string;
  responses?: OrderedResponse[];
  globalLoading?: boolean;
}

const ALL_MODELS: ModelType[] = ['gpt', 'gemini', 'claude', 'grok'];

const MODEL_LABEL: Record<ModelType, string> = {
  gpt:    'GPT',
  gemini: 'Gemini',
  claude: 'Claude',
  grok:   'Grok',
};

const MODEL_COLOR: Record<ModelType, string> = {
  gpt:    '#10a37f',
  gemini: '#4f7ef8',
  claude: '#d97757',
  grok:   '#2d2f3a',
};

const getApiBase = () => {
  return window.location.hostname === 'localhost'
    ? 'http://localhost/chat'
    : '/chat';
};

const Spinner = ({ size = 18, color = '#6b6966' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    style={{ flexShrink: 0, display: 'block' }}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeOpacity="0.2" strokeWidth="2.5" />
    <g>
      <path d="M12 3a9 9 0 0 1 9 9" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <animateTransform
        attributeName="transform" type="rotate"
        from="0 12 12" to="360 12 12"
        dur="0.8s" repeatCount="indefinite"
      />
    </g>
  </svg>
);

const BounceDots = ({ color = '#b0ada8' }: { color?: string }) => (
  <svg width="28" height="10" viewBox="0 0 28 10" style={{ flexShrink: 0 }}>
    {[4, 14, 24].map((cx, i) => (
      <circle key={i} cx={cx} cy="5" r="2.5" fill={color}>
        <animate attributeName="cy" values="5;1.5;5" dur="1.2s" begin={`${i * 0.2}s`} repeatCount="indefinite" />
        <animate attributeName="fill-opacity" values="0.4;1;0.4" dur="1.2s" begin={`${i * 0.2}s`} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
);

function Rag() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'bot',
      text: `안녕하세요.구피티입니다
           질문 하나로 GPT, Gemini, Claude, Grok의 답변을 동시에 비교할 수 있어요.
           무엇이든 편하게 물어보세요 🙂`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [responseCount, setResponseCount] = useState(0);
  const [activeTabById, setActiveTabById] = useState<Record<string, ModelType>>({});

  const chatBoxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, [input]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const question = input;
    const questionId = Date.now().toString();

    const userMessage: Message = {
      id: questionId + '-user',
      role: 'user',
      text: question
    };

    const botPlaceholder: Message = {
      id: questionId,
      role: 'bot',
      text: '',
      globalLoading: true,
      responses: ALL_MODELS.map(p => ({ provider: p, loading: true }))
    };

    setMessages(prev => [...prev, userMessage, botPlaceholder]);
    setInput('');
    setLoading(true);
    setResponseCount(0);
    setTimeout(scrollToBottom, 0);

    let ragContext = '';
    try {
      const ragRes = await axios.post(`${getApiBase()}/ragData`, {
        messages: [
          ...messages.filter(m => m.role === 'user').map(m => ({ role: 'user', content: m.text })),
          { role: 'user', content: question }
        ]
      });
      ragContext = ragRes.data.context || '';
    } catch (e) {
      console.error('RAG 실패', e);
    }

    const payload = {
      messages: [
        ...messages.filter(m => m.role === 'user').map(m => ({ role: 'user', content: m.text })),
        { role: 'user', content: question }
      ],
      ragContext
    };

    const callModel = async (model: ModelType) => {
      const start = Date.now();
      try {
        const res = await axios.post(`${getApiBase()}/llm/${model}`, payload);
        const durationSec = ((Date.now() - start) / 1000).toFixed(2);
        appendModelResponse(questionId, model, {
          reply: res.data.reply,
          model: res.data.model,
          usage: res.data.usage,
          durationSec: Number(durationSec)
        });
      } catch {
        appendModelResponse(questionId, model, { reply: '⚠️ 오류 발생', durationSec: 0 });
      }
    };

    ALL_MODELS.forEach(model => callModel(model));
  };

  const appendModelResponse = (questionId: string, model: ModelType, data: ModelResponse) => {
    setMessages(prev =>
      prev.map(msg => {
        if (msg.id !== questionId || !msg.responses) return msg;
        const currentOrder = msg.responses.filter(r => !r.loading).length + 1;
        const updated = msg.responses.map(r =>
          r.provider === model ? { provider: model, data, loading: false, order: currentOrder } : r
        );
        if (currentOrder === 1) {
          setTimeout(scrollToBottom, 50);
        }
        return { ...msg, responses: updated, globalLoading: false };
      })
    );

    setActiveTabById(prev => {
      if (!prev[questionId]) return { ...prev, [questionId]: model };
      return prev;
    });

    setResponseCount(prev => {
      const next = prev + 1;
      if (next === 1) setLoading(false);
      return next;
    });
  };

  return (
    <div className="rag-wrap">
      <div className="chat-container">
        <div className="chat-header">
          <span className="title-main">ChatKooPT</span>
        </div>

        <div className="chat-box" ref={chatBoxRef}>
          {messages.map(msg => (
            <div key={msg.id} className={`chat-bubble ${msg.role}`}>

              {msg.role === 'user' && <MarkdownRenderer content={msg.text} />}

              {msg.role === 'bot' && msg.responses && (
                <>
                  {msg.globalLoading ? (
                    <div className="global-loading-wrap">
                      <Spinner size={17} color="#b0ada8" />
                      <span className="global-loading-label">Analyzing</span>
                      <BounceDots color="#b0ada8" />
                    </div>
                  ) : (
                    <>
                      <div className="inner-tab-buttons">
                        {[...msg.responses]
                          .sort((a, b) => {
                            if (a.order && b.order) return a.order - b.order;
                            if (a.order) return -1;
                            if (b.order) return 1;
                            return 0;
                          })
                          .map(r => (
                          <button
                            key={r.provider}
                            data-provider={r.provider}
                            className={[
                              'tab-btn',
                              activeTabById[msg.id] === r.provider ? 'active' : '',
                              r.loading ? 'is-loading' : ''
                            ].filter(Boolean).join(' ')}
                            onClick={() =>
                              setActiveTabById(prev => ({ ...prev, [msg.id]: r.provider }))
                            }
                          >
                            <span className="provider-name">{MODEL_LABEL[r.provider]}</span>
                            {!r.loading && r.order && (
                              <span className="order-badge">
                                {r.order === 1 && '🥇'}
                                {r.order === 2 && '🥈'}
                                {r.order === 3 && '🥉'}
                                {r.order > 3 && `#${r.order}`}
                              </span>
                            )}
                            {r.loading && (
                              <Spinner size={12} color={MODEL_COLOR[r.provider]} />
                            )}
                          </button>
                        ))}
                      </div>

                      {msg.responses
                        .filter(r => r.provider === activeTabById[msg.id])
                        .map(r => (
                          <div
                            key={r.provider}
                            className="bot-content"
                            data-active={r.provider}
                          >
                            {r.loading ? (
                              <div className="typing">{r.provider} 응답 작성 중...</div>
                            ) : (
                              <>
                                <div className="model-name">
                                  <strong>{r.data?.model}</strong>
                                  <span>· {r.data?.durationSec}s</span>
                                </div>
                                <div className="response-accent">
                                  <MarkdownRenderer content={r.data?.reply || ''} />
                                </div>
                                {r.data?.usage && (
                                  <div className="usage">
                                    {r.data.usage.total_tokens.toLocaleString()} tokens
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                    </>
                  )}
                </>
              )}

              {msg.role === 'bot' && !msg.responses && (
                <MarkdownRenderer content={msg.text} />
              )}
            </div>
          ))}
        </div>

        <div className="chat-input">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="무엇이든 물어보세요..."
            disabled={loading && responseCount === 0}
            rows={1}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button onClick={handleSend} disabled={loading && responseCount === 0}>➤</button>
        </div>

        <div className="chat-footer">ChatKooPT · BackendLab © 2025</div>
      </div>
    </div>
  );
}

export default Rag;